import React from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { API, getBrand } from "utils/Helper";
import styled, { keyframes } from "styled-components";
import { Helmet } from "react-helmet";

const pulse = keyframes`
  0% { opacity: 0.6; }
  50% { opacity: 1; }
  100% { opacity: 0.6; }
`;

const SkeletonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  width: 100%;
  padding: 40px 24px;
  max-width: 1000px;
  margin: 0 auto;
`;

const SkeletonLine = styled.div`
  height: 18px;
  background-color: #2e3136;
  border-radius: 4px;
  animation: ${pulse} 1.5s infinite ease-in-out;
  width: ${props => props.width || "100%"};
`;

const Container = styled.div`
  padding: 40px 24px;
  color: white;
  max-width: 1000px;
  margin: 0 auto;
  min-height: 60vh;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 24px;
  border-bottom: 1px solid #2e3136;
  padding-bottom: 16px;
  color: #ffffff;
`;

const DynamicCmsPage = () => {
  const { pageKey } = useParams();

  const { data: page, isLoading, error } = useQuery({
    queryKey: ["cmsPage", pageKey],
    queryFn: async () => {
      const res = await axios.get(`${API}/cms-pages/${pageKey}`);
      return res.data?.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <SkeletonWrapper>
        <SkeletonLine width="40%" style={{ height: "36px", marginBottom: "20px" }} />
        <SkeletonLine width="100%" />
        <SkeletonLine width="95%" />
        <SkeletonLine width="97%" />
        <SkeletonLine width="80%" />
        <br />
        <SkeletonLine width="35%" style={{ height: "24px", marginBottom: "10px" }} />
        <SkeletonLine width="92%" />
        <SkeletonLine width="88%" />
        <SkeletonLine width="75%" />
      </SkeletonWrapper>
    );
  }

  if (error || !page) {
    return (
      <Container>
        <Helmet>
          <title>Page Not Found | {getBrand()}</title>
        </Helmet>
        <Title>Page Not Found</Title>
        <p className="text-gray-400 font-light mb-4 text-sm">
          The page you are looking for does not exist or has been moved.
        </p>
      </Container>
    );
  }

  return (
    <Container>
      <Helmet>
        <title>{page.meta_title || page.title || getBrand()}</title>
        {page.meta_description && (
          <meta name="description" content={page.meta_description} />
        )}
      </Helmet>
      <Title>{page.title}</Title>
      <div 
        className="text-gray-200 font-light text-sm leading-relaxed cms-content-render"
        dangerouslySetInnerHTML={{ __html: page.content }}
      />
    </Container>
  );
};

export default DynamicCmsPage;
